import { getTranslate } from 'legacy-src/utilities/i18n'
import Markdown from 'markdown-to-jsx'
import { useRouter } from 'next/router'
import React from 'react'
import { z } from 'zod'

import type { ExtendedDiagnosisFields } from '@/legacy-src/components/diagnosis/client/types'
import { parseDiagnosisParams } from '@/legacy-src/components/diagnosis/client/utils'
import {
  TherapistsPreviewInRegistration,
  type TherapistsPreviewInRegistrationProps,
} from '@/legacy-src/components/diagnosis/registration/TherapistsPreviewInRegistration'
import CustomerSupportBlock from '@/legacy-src/components/pageBlocks/blocks/CustomerSupportBlock'
import RegisterForm from '@/legacy-src/components/register/RegisterForm'
import SupportWidget from '@/legacy-src/components/support/SupportWidget'
import { cn } from '@/legacy-src/lib/utils'
import { AuthPageMode, getAuthPageModeFromQuery } from '@/legacy-src/utilities/authUtils'

import { Text } from '@/src/domains/design-system/components/Text'

import RegisterBenefits from '../components/register/RegisterBenefits'
import SignUpHeader from '../components/SignUpHeader'

type Props = {
  therapistId?: number
}

export enum DiagnosisRegistrationVersion {
  ORIGINAL = 'original',
  VERSION_A = 'version_a',
  VERSION_B = 'version_b',
}

const QuerySchema = z.object({
  registrationVersion: z
    .nativeEnum(DiagnosisRegistrationVersion)
    .optional()
    .default(DiagnosisRegistrationVersion.ORIGINAL),
})

const previewVersionMap: Record<DiagnosisRegistrationVersion, TherapistsPreviewInRegistrationProps['version']> = {
  [DiagnosisRegistrationVersion.ORIGINAL]: undefined,
  [DiagnosisRegistrationVersion.VERSION_A]: 'versionA',
  [DiagnosisRegistrationVersion.VERSION_B]: 'versionB',
}

const headerMap: Record<DiagnosisRegistrationVersion, string> = {
  [DiagnosisRegistrationVersion.ORIGINAL]: '',
  [DiagnosisRegistrationVersion.VERSION_A]: 'diagnosisTest.register.versionA.header',
  [DiagnosisRegistrationVersion.VERSION_B]: 'diagnosisTest.register.versionB.header',
}

const RegistrationContainer: React.FC<Props> = ({ therapistId }) => {
  const { t } = getTranslate()
  const router = useRouter()

  const mode = getAuthPageModeFromQuery(router.query)

  const { registrationVersion: version } = QuerySchema.parse(router.query)

  const isDiagnosis = mode === AuthPageMode.DIAGNOSIS
  const diagnosis = isDiagnosis
    ? (parseDiagnosisParams(router.query.params as string) as ExtendedDiagnosisFields)
    : null

  const isHeaderVisible = version !== DiagnosisRegistrationVersion.ORIGINAL
  const isSupportVisible = version !== DiagnosisRegistrationVersion.ORIGINAL

  return (
    <div className="signup">
      <div className="container">
        <SignUpHeader />
        <div className={cn('m-auto flex w-full max-w-[680px] flex-col')}>
          {isDiagnosis && diagnosis ? (
            <TherapistsPreviewInRegistration diagnosis={diagnosis} version={previewVersionMap[version]} />
          ) : (
            <div className={cn('signup__title', 'text-center')}>
              <h1 className="h2 text-color-secondary">{t('registration.title')}</h1>
            </div>
          )}

          <div className="relative flex flex-col">
            {isHeaderVisible ? (
              <div className="bg-default rounded-t-3xl border-b-2 border-l-0 border-r-0 border-t-0 border-solid border-b-sand-500 px-5 py-5 tablet:px-20">
                <Text element="p" className="m-0 text-center">
                  <Markdown>{t(headerMap[version])}</Markdown>
                </Text>
              </div>
            ) : null}
            <div
              className={cn(
                'bg-default flex w-full flex-col justify-center overflow-hidden rounded-b-3xl px-5 py-5 tablet:px-20 tablet:py-12',
                {
                  'rounded-t-3xl': !isHeaderVisible,
                },
              )}
            >
              <RegisterForm
                isDiagnosis={isDiagnosis}
                therapistId={therapistId}
                gtmEventData={
                  isDiagnosis
                    ? {
                        registrationVersion: version,
                      }
                    : undefined
                }
              />
            </div>
          </div>

          <div className="mt-10 w-full max-w-[680px] px-5">
            <RegisterBenefits />
          </div>
        </div>
      </div>
      {isSupportVisible ? <CustomerSupportBlock /> : null}
      <SupportWidget />
    </div>
  )
}

export default RegistrationContainer
